/**
 * Utility functions for dynamically loading scripts and styles
 */

/**
 * Options for loading a script.
 */
export interface LoadScriptOptions {
  defer?: boolean;
  async?: boolean;
  id?: string;
}

/**
 * Loads a generic script tag with a promise.
 * @param url The source URL of the script.
 * @param options Optional configuration for the script tag.
 * @returns A promise that resolves when the script is loaded.
 */
export function loadScript(
  url: string,
  options: LoadScriptOptions = {},
): Promise<void> {
  const { defer = false, async = false, id } = options;

  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${url}"]`)) {
      console.debug(`[Loader] Script already injected: ${url}`);
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = url;
    script.defer = defer;
    script.async = async;
    if (id) script.id = id;

    script.onload = () => {
      console.log(`[Loader] Script loaded: ${url}`);
      resolve();
    };
    script.onerror = (e) => {
      console.error(`[Loader] Script failed to load: ${url}`, e);
      reject(e);
    };
    document.head.appendChild(script);
  });
}

/**
 * Loads a generic link stylesheet tag with a promise.
 * @param url The href URL of the stylesheet.
 * @param id Optional identifier for the link tag.
 * @returns A promise that resolves when the style is loaded.
 */
export function loadStyle(url: string, id?: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const link = document.createElement("link");
    if (id) link.id = id;
    link.rel = "stylesheet";
    link.href = url;
    // IMPORTANT: Do NOT set link.disabled = true here.
    // In many browsers, disabled link tags do not fire 'onload', causing a deadlock.
    link.onload = () => {
      console.log(`[Loader] Style loaded: ${id || url}`);
      resolve();
    };
    link.onerror = (e) => {
      console.error(`[Loader] Style failed to load: ${id || url}`, e);
      reject(e);
    };
    document.head.appendChild(link);
  });
}
